import React, { useRef, useState } from 'react'
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, ChevronDown, Clock } from 'lucide-react'
import { Navbar, Footer } from '../components/SharedLayout'
import MetaTags from '../components/MetaTags'
import {
  marketingColors as colors,
  marketingBrand,
  PAPER,
  DARK,
  BOOKING_CALENDAR_URL,
  marketingEase as ease,
} from '../lib/marketingBrand'
import {
  ACCENT,
  HeroEmboss,
  MarketingReveal,
  MKRule,
  MKH2,
  MKImageFrame,
  ScreenedBg,
  SketchBreak,
  PaperTextureOverlay,
} from '../components/marketing/MarketingChrome'

const btnPrimary =
  'group inline-flex items-center gap-3 px-10 py-5 text-[15px] font-medium font-sans tracking-[0.06em] uppercase text-white transition-all duration-500 hover:brightness-110'
const btnSecondary =
  'group inline-flex items-center gap-3 px-10 py-5 text-[15px] font-medium font-sans tracking-[0.06em] uppercase transition-all duration-500 hover:opacity-70 border'

const IMG = 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/'

const Hero: React.FC = () => {
  const ref = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] })
  const y = useTransform(scrollYProgress, [0, 1], [0, 150])
  const opacity = useTransform(scrollYProgress, [0, 0.6], [1, 0])

  const trustItems = [
    'Single Point of Contact',
    'Licensed Professionals',
    'Serving the Des Moines Metro',
    'Same-Day Response Guaranteed',
  ]

  return (
    <section
      ref={ref}
      className="relative min-h-screen flex flex-col justify-center overflow-hidden"
      style={{ backgroundColor: PAPER }}
    >
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={`${IMG}singlefamilyhome.jpg`}
          alt=""
          className="w-full h-full object-cover"
        />
      </motion.div>

      <div
        className="absolute inset-0 z-0"
        style={{
          background: `linear-gradient(105deg, ${PAPER}f0 0%, ${PAPER}e0 35%, ${PAPER}80 55%, ${PAPER}40 72%, transparent 100%)`,
        }}
      />
      <div
        className="absolute inset-0 z-0"
        style={{ background: `linear-gradient(180deg, ${PAPER}e8 0%, transparent 18%, transparent 75%, ${PAPER}f5 100%)` }}
      />
      <HeroEmboss />

      <motion.div style={{ opacity }} className="relative z-10 pt-32 pb-16 lg:pt-40 lg:pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 xl:px-20">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease, delay: 0.15 }}
            >
              <MKRule wide />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease, delay: 0.2 }}
              className="font-serif mb-6"
              style={{
                fontSize: 'clamp(2.5rem, 5.5vw, 4.5rem)',
                lineHeight: 1.05,
                letterSpacing: '-0.03em',
                color: colors.tealDark,
              }}
            >
              Stop Managing Your Home.
              <br />
              <span className="italic" style={{ color: colors.copper }}>
                Start Living In It.
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease, delay: 0.4 }}
              className="text-lg lg:text-xl leading-[1.85] max-w-2xl mb-10 font-sans"
              style={{ color: colors.drillbitSkies }}
            >
              Hudson My Home™ membership is ongoing, proactive home management handled by a dedicated
              professional. From seasonal maintenance to system care, we stay on top of everything so
              your home is always protected, always ahead, and never a burden.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease, delay: 0.6 }}
            >
              <Link
                to="/quiz"
                className={btnSecondary}
                style={{ borderColor: colors.plaster, color: colors.drillbitSkies }}
              >
                Discover Your Archetype
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease, delay: 0.8 }}
        className="relative z-10 mt-auto"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 pb-12">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            {trustItems.map((item, i) => (
              <React.Fragment key={item}>
                {i > 0 && (
                  <span className="hidden md:inline w-px h-3 self-center" style={{ backgroundColor: `${colors.copper}55` }} />
                )}
                <span className="text-xs font-semibold uppercase tracking-[0.16em]" style={{ color: colors.drillbitSkies }}>
                  {item}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  )
}

const ProblemSection: React.FC = () => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section ref={ref} className="relative py-28 md:py-36 lg:py-48 overflow-hidden" style={{ backgroundColor: PAPER }}>
      <PaperTextureOverlay opacity={0.035} />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <div>
            <motion.h2
              initial={{ opacity: 0, y: 60 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease }}
              className="font-serif mb-8"
              style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
                color: colors.nearBlack,
              }}
            >
              The Honey-Do List Never Ends.
              <br />
              Neither Does the Mental Load.
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, ease, delay: 0.1 }}
              className="text-lg leading-[1.85] mb-6 font-sans"
              style={{ color: colors.drillbitSkies }}
            >
              Between careers, family, and everything else, home maintenance often falls to the
              bottom of the list. It&apos;s not that you don&apos;t care, it&apos;s that your time
              and attention are already stretched.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, ease, delay: 0.2 }}
              className="text-lg leading-[1.85] font-sans"
              style={{ color: colors.drillbitSkies }}
            >
              That mental burden — organizing all the things, tracking what&apos;s due, wondering
              what you&apos;re forgetting — is exhausting. You deserve to enjoy your home without
              carrying that weight. Hudson My Home™ exists so you can hand off the list and finally
              get back to living.
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.9, ease, delay: 0.2 }}
          >
            <MKImageFrame
              src={`${IMG}babywithhandsovermom.jpg`}
              alt="Family enjoying their home"
            />
          </motion.div>
        </div>
      </div>
    </section>
  )
}

const ProcessSection: React.FC = () => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })

  const steps = [
    {
      num: '01',
      title: 'Get Started',
      desc: 'Start by requesting a consultation. This allows us to learn about your home and confirm we can deliver the level of care it deserves. Membership is limited to ensure every home receives dedicated, high-touch attention.',
    },
    {
      num: '02',
      title: 'MyHome Chat™',
      desc: "We schedule a quick MyHome Chat\u2122 to learn about your home and how you currently manage it. This is also where we match you with your Hudson \u2014 the dedicated home professional who will manage your home going forward. It\u2019s an opportunity to ask questions and see how our proactive approach works.",
    },
    {
      num: '03',
      title: 'In-Home Visit',
      desc: "Your Hudson visits your property to walk through the home and understand how it\u2019s currently performing. Together, you\u2019ll review major systems, maintenance history, and key areas so your Hudson can fully understand how to care for it moving forward.",
    },
    {
      num: '04',
      title: 'Receive Your Personalized Plan',
      desc: "From the visit, we generate your MyHome Score™, which reflects the overall condition of your home. Using that score, we create your MyHome Blueprint™, a proprietary five-year plan designed to keep your home running smoothly through thoughtful maintenance, smart improvements, and proactive care.",
    },
    {
      num: '05',
      title: 'Live In Your Home Like A Guest In Ours.',
      desc: "With your plan in place, monthly or quarterly visits begin. Your Hudson home manager coordinates maintenance, oversees trusted vendors, and keeps everything on track so your home is expertly cared for behind the scenes and you\u2019re free to simply enjoy living in it. Members also enjoy holiday light coordination, seasonal donation and junk pickup scheduling, and priority access to our vetted contractor network.",
    },
  ]

  return (
    <section ref={ref} className="py-28 md:py-36 lg:py-48" style={{ backgroundColor: colors.cream }}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <MarketingReveal className="text-center max-w-3xl mx-auto mb-20">
          <div className="flex justify-center mb-8">
            <div className="h-px w-16 lg:w-24" style={{ backgroundColor: colors.copper }} />
          </div>
          <MKH2 className="text-center">
            Welcome to a Different Standard.
            <br />
            <span className="italic" style={{ color: colors.copper }}>
              Your Home Deserves Nothing Less.
            </span>
          </MKH2>
        </MarketingReveal>

        <div className="space-y-6 max-w-4xl mx-auto">
          {steps.map((step, i) => (
            <motion.div
              key={step.num}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, ease, delay: 0.1 + i * 0.1 }}
              className="flex gap-8 bg-white/90 backdrop-blur-[2px] p-8 lg:p-10"
              style={{ border: `1px solid ${colors.plaster}55` }}
            >
              <span
                className="text-5xl lg:text-6xl font-serif flex-shrink-0 leading-none"
                style={{ color: `${colors.teal}25` }}
              >
                {step.num}
              </span>
              <div>
                <h3
                  className="text-xl font-medium mb-3"
                  style={{ color: colors.nearBlack }}
                >
                  {step.title}
                </h3>
                <p className="text-base leading-[1.85] font-sans" style={{ color: colors.drillbitSkies }}>{step.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

const FeatureCallout: React.FC = () => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section ref={ref} className="relative py-32 md:py-40 lg:py-48 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={`${IMG}services.jpg`}
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      <div
        className="absolute inset-0"
        style={{ background: `linear-gradient(115deg, ${DARK}f2 0%, ${colors.tealDark}e8 50%, ${colors.teal}55 100%)` }}
      />
      <div className="pointer-events-none absolute inset-0 opacity-[0.08] z-[1]" aria-hidden>
        <ScreenedBg
          src={ACCENT.penBlueprint}
          blend="soft-light"
          opacity={0.5}
          className="absolute right-[-5%] top-1/2 w-[min(420px,45vw)] -translate-y-1/2 object-contain"
        />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 60 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease }}
          className="font-serif text-white mb-10"
          style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
          }}
        >
          Meticulously Maintained
          <br />
          <span className="italic" style={{ color: colors.copper }}>
            Before You Even Have to Ask.
          </span>
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease, delay: 0.2 }}
        >
          <Link
            to="/pricing"
            className="group inline-flex items-center gap-3 px-10 py-5 text-[15px] font-medium font-sans tracking-[0.06em] uppercase transition-all duration-500 hover:opacity-90"
            style={{ backgroundColor: marketingBrand.white, color: colors.tealDark }}
          >
            See Sample Plans
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </motion.div>
      </div>
    </section>
  )
}

const SameDayGuarantee: React.FC = () => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section ref={ref} className="relative py-28 md:py-36 lg:py-48 overflow-hidden" style={{ backgroundColor: PAPER }}>
      <PaperTextureOverlay opacity={0.03} />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease }}
          >
            <MKImageFrame src={`${IMG}keyindoor.jpg`} alt="Key in door" />
          </motion.div>

          <div>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, ease }}
              className="flex items-center gap-3 mb-6"
            >
              <Clock className="w-6 h-6" style={{ color: colors.teal }} />
              <span
                className="text-sm tracking-[0.15em] uppercase"
                style={{ color: colors.copper }}
              >
                MyHome Guarantee™
              </span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 60 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease, delay: 0.1 }}
              className="font-serif mb-8"
              style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
                color: colors.nearBlack,
              }}
            >
              Your Home Doesn&apos;t Wait.
              <br />
              Neither Do We.
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, ease, delay: 0.2 }}
              className="text-lg leading-[1.85] mb-6 font-sans"
              style={{ color: colors.drillbitSkies }}
            >
              When something urgent comes up, you shouldn&apos;t have to wait. The MyHome
              Guarantee™ ensures every urgent maintenance request receives a same-day response, no
              exceptions.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, ease, delay: 0.3 }}
              className="text-lg leading-[1.85] mb-10 font-sans"
              style={{ color: colors.drillbitSkies }}
            >
              A home that&apos;s truly handled means you&apos;re never left wondering if someone is
              coming. That&apos;s our standard.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, ease, delay: 0.4 }}
            >
              <Link to="/pricing" className={btnPrimary} style={{ backgroundColor: colors.copper }}>
                Explore Pricing
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  )
}

const TeamSection: React.FC = () => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section ref={ref} className="relative py-32 md:py-40 lg:py-48 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={`${IMG}Meet%20the%20Team%20background.jpg`}
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/85 to-white/60" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="max-w-2xl">
          <motion.h2
            initial={{ opacity: 0, y: 60 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease }}
            className="font-serif mb-12"
            style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              color: colors.nearBlack,
            }}
          >
            Meet the Team Behind
            <br />
            Hudson My Home™
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, ease, delay: 0.2 }}
            className="bg-white p-8 lg:p-10 shadow-lg"
          >
            <div className="flex items-center gap-4 mb-6">
              <img
                src="https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/Joe.png"
                alt="Joe Schafbuch — Founder & Visionary"
                className="w-16 h-16 rounded-full object-cover ring-1 ring-black/10"
                loading="lazy"
              />
              <div>
                <h3
                  className="text-xl font-medium"
                  style={{ color: colors.nearBlack }}
                >
                  Joe Schafbuch
                </h3>
                <p className="text-sm" style={{ color: colors.copper }}>
                  Founder &amp; Visionary
                </p>
              </div>
            </div>

            <p className="text-base leading-relaxed text-gray-600 mb-8">
              Joe built Hudson My Home™ because he lived the problem firsthand. As a father and
              entrepreneur, he knew the moments spent with family were priceless&mdash;and too often
              lost to home maintenance. His vision is straightforward: your home should be a source
              of comfort, not a source of stress.
            </p>

            <div className="flex flex-wrap items-center gap-6">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-8 py-4 text-sm font-medium font-sans tracking-[0.06em] uppercase text-white transition-all duration-500 hover:brightness-110"
                style={{ backgroundColor: colors.copper }}
              >
                Get to Know Us
              </Link>
              <Link
                to="/about"
                className="group inline-flex items-center gap-2 text-sm font-medium transition-colors duration-300"
                style={{ color: colors.drillbitSkies }}
              >
                See More
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

const FAQSection: React.FC = () => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const faqs = [
    {
      q: 'What does a Hudson My Home™ membership include?',
      a: 'Your membership includes a dedicated home manager, a personalized MyHome Blueprint™ five-year plan, monthly or quarterly in-home visits, vendor coordination and oversight, same-day emergency response through the MyHome Guarantee™, and complete documentation of all work performed on your home.',
    },
    {
      q: 'How is this different from a handyman or home warranty?',
      a: 'A handyman fixes things when they break. A home warranty reimburses you after the fact. Hudson My Home™ is proactive — we prevent issues before they start, manage your entire home plan, and serve as a single point of contact for everything your home needs.',
    },
    {
      q: 'What areas do you currently serve?',
      a: 'We currently serve homeowners throughout the Des Moines metro area, including Urbandale, West Des Moines, Waukee, Johnston, Ankeny, and surrounding communities.',
    },
    {
      q: 'What is the MyHome Guarantee™?',
      a: 'The MyHome Guarantee™ is our commitment that every urgent maintenance request receives a same-day response — no exceptions. When something comes up, you will never be left wondering if someone is on the way.',
    },
    {
      q: 'How do I get started?',
      a: 'Start by completing a short application on our website. From there, we schedule a MyHome Chat™ to learn about your home, followed by an in-home visit to create your personalized plan.',
    },
  ]

  return (
    <section ref={ref} className="py-28 md:py-36 lg:py-48" style={{ backgroundColor: colors.cream }}>
      <div className="max-w-3xl mx-auto px-6 md:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease }}
          className="text-center mb-16"
        >
          <h2
            className="font-serif"
            style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              color: colors.nearBlack,
            }}
          >
            Have Any Questions?
            <br />
            Let&apos;s Answer Them.
          </h2>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, ease, delay: 0.1 + i * 0.05 }}
              className="bg-white shadow-sm"
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between p-6 text-left"
              >
                <span
                  className="text-lg font-medium pr-4"
                  style={{ color: colors.nearBlack }}
                >
                  {faq.q}
                </span>
                <ChevronDown
                  className="w-5 h-5 flex-shrink-0 transition-transform duration-300"
                  style={{
                    color: colors.copper,
                    transform: openIndex === i ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              </button>
              <AnimatePresence>
                {openIndex === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <p className="px-6 pb-6 text-base leading-relaxed text-gray-600">
                      {faq.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

const FooterCTA: React.FC = () => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section ref={ref} className="py-28 md:py-36 lg:py-48" style={{ backgroundColor: colors.tealDark }}>
      <div className="max-w-[1000px] mx-auto px-6 md:px-10 lg:px-16 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 60 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease }}
          className="font-serif text-white mb-4"
          style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
          }}
        >
          We Maintain Your Home
          <br />
          So You Don&apos;t Have To.
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease, delay: 0.12 }}
          className="font-serif italic mt-4"
          style={{ fontSize: 'clamp(1.15rem, 2vw, 1.5rem)', color: colors.copper }}
        >
          Everyone deserves a Hudson.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease, delay: 0.2 }}
          className="mt-10"
        >
          <Link to="/membership" className={btnPrimary} style={{ backgroundColor: colors.copper }}>
            Discover Home Membership
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
          <p className="text-sm text-grey-400 mt-2">No commitment required. Cancel anytime.</p>
        </motion.div>
      </div>
    </section>
  )
}

const PricingCallout: React.FC = () => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section ref={ref} className="py-28 md:py-36 lg:py-48" style={{ backgroundColor: PAPER }}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease }}
          className="max-w-3xl mx-auto"
        >
          <div className="border-l-4 pl-8 py-6" style={{ borderColor: colors.copper }}>
            <p className="font-serif text-2xl lg:text-3xl mb-4" style={{ color: colors.nearBlack }}>
              Membership starts at $399/month
            </p>
            <ul className="space-y-2 text-base text-gray-600 leading-relaxed mb-6">
              <li>Your own Hudson — a dedicated home professional, year-round proactive care</li>
              <li>Same-day response guarantee</li>
              <li>MyHome Blueprint™ + MyHome Vault™ included</li>
            </ul>
            <p className="text-sm font-medium mb-6" style={{ color: colors.copper }}>
              Founding member pricing available — ask about locked-in rates.
            </p>
            <a
              href={BOOKING_CALENDAR_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={btnPrimary}
              style={{ backgroundColor: colors.copper }}
            >
              Book Your Consultation
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </a>
          </div>

          <p className="mt-8 text-base text-gray-600 leading-relaxed">
            We offer a 30-day satisfaction guarantee. If you don&apos;t see the value within your
            first month, we&apos;ll refund your membership fee entirely — no questions asked.
          </p>
        </motion.div>
      </div>
    </section>
  )
}

const MembershipPage: React.FC = () => (
  <div className="min-h-screen" style={{ backgroundColor: marketingBrand.tapeTurquoise }}>
    <MetaTags
      title="Home Membership | Hudson My Home"
      description="A complete home management membership. Your own Hudson, proactive seasonal care, vendor coordination, and the MyHome Blueprint — starting at $399/month."
    />
    <Navbar />
    <main>
      <Hero />
      <ProblemSection />
      <SketchBreak />
      <ProcessSection />
      <FeatureCallout />
      <SameDayGuarantee />
      <TeamSection />
      <PricingCallout />
      <FAQSection />
      <FooterCTA />
    </main>
    <Footer />
  </div>
)

export default MembershipPage
